import { useState, useMemo } from "react";
import LocalRsnChnlProvider, { LocalRsnApiResponse, ProductType } from "@directv-digital/dds.apis.local-rsn-chnls";
import { buildLocalChannelObject } from "src/apis/channels-service-locals";
import { County } from "@directv-digital/dds.apis.county-lookup";

/**
 * Custom hook for managing zipcode module state and handlers
 * @param chnlSrc - Channel source identifier (e.g., "dtvClup" for satellite, "streamclup" for internet)
 * @returns Object containing state and zipcode module callbacks
 */
export const useZipcodeHandlers = (chnlSrc?: string) => {
  // Determine product type from chnlSrc (only "dtvClup" is satellite)
  const productType = chnlSrc === "dtvClup" ? ProductType.SATELLITE : ProductType.STREAM;
  // Internal state management
  const [localRsnChnlsData, setLocalRsnChnlsData] = useState<{ localChannels: any[]; rsnChannels: any[] }>({
    localChannels: [],
    rsnChannels: []
  });
  const [dmaData, setDmaData] = useState({
    dma: "",
    nielsenDma: ""
  });
  const [_apiError, setApiError] = useState<boolean>(false);
  const [_isLoadingLocalRsn, setIsLoadingLocalRsn] = useState<boolean>(false);
  const [fetchedZipcodeModuleData, setFetchedZipcodeModuleData] = useState(null);

  // Memoized handlers
  const handlers = useMemo(() => {
    /**
     * Sets loading state when zipcode search is initiated
     */
    const setLoadingLocalRsnChannels = () => {
      setLocalRsnChnlsData({
        localChannels: [],
        rsnChannels: []
      });
      setIsLoadingLocalRsn(true);
    };

    /**
     * Processes local/RSN channel API response and builds channel objects
     * @param channels - API response with local and RSN channels
     * @param dma - DTV DMA code
     * @param nielsenDma - Nielsen DMA code
     */
    const addLocalRsnChnls = (channels: LocalRsnApiResponse | null, dma?: string, nielsenDma?: string) => {
      const { localChannels = [], rsnChannels = [] } = channels || {};

      setLocalRsnChnlsData(() => ({
        localChannels:
          localChannels?.length > 0 ?
            localChannels?.map?.((chnl: any) => buildLocalChannelObject(chnl, productType)) :
            [],
        rsnChannels:
          rsnChannels?.length > 0 ?
            rsnChannels?.map?.((chnl: any) => LocalRsnChnlProvider?.buildChannelObject({
              ...chnl,
              logo: chnl?.logo?.split("rsn-channels/")?.[1]
            }, "rsn", productType)) :
            []
      }));

      setDmaData({
        dma: dma || "",
        nielsenDma: nielsenDma || ""
      });

      setApiError(false);
      setIsLoadingLocalRsn(false);
    };

    /**
     * Handles errors from local/RSN channel API
     */
    const handleLocalRsnApiError = () => {
      setApiError(true);
      setIsLoadingLocalRsn(false);
      setLocalRsnChnlsData(() => ({
        localChannels: [],
        rsnChannels: []
      }));
    };

    /**
     * Caches zipcode search results for future use
     * @param zipcode - Searched zipcode
     * @param counties - County lookup results
     * @param rsnFee - RSN fee for the area
     * @param localRsnChnls - Local/RSN channel data
     */
    const onCountySearchDone = (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null) => {
      setFetchedZipcodeModuleData(() => ({
        zipcode,
        counties,
        rsnFee,
        localRsnChnls
      }));
    };

    return {
      setLoadingLocalRsnChannels,
      addLocalRsnChnls,
      handleLocalRsnApiError,
      onCountySearchDone
    };
  }, [productType]);

  return {
    // State that component needs
    localRsnChnlsData,
    dmaData,
    fetchedZipcodeModuleData,
    // Handlers
    ...handlers
  };
};
